import { ActionEditDots } from '@components/AppMenu';
import { DeleteConfirmPopup } from '@components/DeleteConfirm';
import PermissionTooltip from '@components/PermissionTooltip';
import useUrlPermission from '@hooks/url-permission';
import {
  forwardRef,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useState,
} from 'react';
import { cn } from '@skupreme/shared/ui';
import { AppTableRoot, type AppTableRootProps } from '@components/AppTable';

export const ShadowTable = forwardRef<HTMLTableElement, AppTableRootProps>(
  function ShadowTableRoot({ className, ...rest }, ref) {
    return (
      <AppTableRoot
        ref={ref}
        {...rest}
        className={cn(
          'sk:[&_thead_th]:bg-surface! sk:[&_thead_th]:text-xs sk:[&_thead_th]:leading-[14px] sk:[&_thead_th]:font-normal! sk:[&_thead_th]:text-fg-tertiary!',
          className,
        )}
      />
    );
  },
);

export function ResponsiveMultiLineColumnLabel({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cn('sk:gap-1 sk:max-[1230px]:flex-col', className)}
    />
  );
}

/* The thumbnail dims and the action dots replace the side panel while the cell is hovered. */
const IMAGE_CELL = cn(
  'sk:relative',
  'sk:[&_.action-container]:inset-0 sk:[&_.action-container]:m-auto sk:[&_.action-container]:size-10 sk:[&_.action-container]:bg-transparent sk:[&_.action-container]:shadow-none',
  'sk:[&_.action-container_button]:size-10',
  'sk:[&:hover_img]:opacity-30 sk:[&:hover_.action-container]:opacity-100',
);

const ACTION_MENU_ITEM = cn(
  'sk:flex sk:items-center sk:rounded-option sk:text-fg sk:hover:bg-fill-quaternary',
  'sk:[&_svg]:mr-1.5 sk:[&.danger]:text-danger',
  'sk:[&.disabled]:cursor-not-allowed! sk:[&.disabled]:opacity-70 sk:[&.disabled_div]:cursor-not-allowed',
);

/* A row action. Callers build these inline, so most fields are optional. */
export type ImageCellAction = {
  title: string;
  icon?: ReactNode;
  className?: string;
  disabled?: boolean;
  showConfirmation?: boolean;
  content?: ReactNode;
  /* Read by the shared menu shape; this cell's dialog keeps its own "Yes". */
  confirmButtonTitle?: string;
  cancelButtonTitle?: string;
  onClick: (e?: MouseEvent) => void;
};

type ImageWithActionCellProps = {
  image: ReactNode;
  menus?: ImageCellAction[];
  /* Required as it always was; pass `undefined` for none. */
  className: string | undefined;
  position?: string;
};

export function ImageWithActionCell({
  image,
  menus,
  className,
  position = 'right center',
}: ImageWithActionCellProps) {
  const actions = menus || [];
  const { disabled, hidden } = useUrlPermission();
  const [open, setOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<ImageCellAction | null>(
    null,
  );

  const actionClick = (e: MouseEvent, item: ImageCellAction) => {
    if (item.showConfirmation) {
      setCurrentAction(item);
      setOpen(true);
    } else {
      item.onClick(e);
    }
  };

  return (
    <>
      {actions.length > 0 ? (
        // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: keeps a click on the thumbnail or its menu from reaching the row.
        <div
          className={cn(IMAGE_CELL, className)}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          {image}
          <ActionEditDots preventChangingBg position={position}>
            {actions.map(item => (
              // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: legacy menu rows, matched by the row menus that share this list.
              <div
                key={item.title}
                onClick={e => {
                  if (!disabled) actionClick(e, item);
                }}
                className={cn(
                  ACTION_MENU_ITEM,
                  item.className,
                  disabled && 'disabled',
                )}
              >
                <PermissionTooltip
                  disabled={disabled}
                  hidden={hidden}
                  position="right center"
                  wrapperClassName="sk:p-0! sk:flex! sk:items-center!"
                >
                  {item.icon}
                  {item.title}
                </PermissionTooltip>
              </div>
            ))}
          </ActionEditDots>
        </div>
      ) : (
        image
      )}
      <DeleteConfirmPopup
        onClose={() => setOpen(false)}
        open={open}
        onConfirm={() => currentAction?.onClick()}
        noButtonTitle={currentAction?.cancelButtonTitle}
        content={currentAction?.content}
      />
    </>
  );
}
